import { CardSize, DetailSize, ViewKind } from '@optima-stride-ai/core/type-based-views';
import { EntityViewComponentLocationType, IEntityViewComponent } from '@optima-stride-ai/optima-app-framework';
import { environment } from '../../environments/environment';

/**
 * EntityTypeView rows name these keys in their `component` field.
 * For typed Small/Medium/Large cards, configure a Card row at the matching size per entity type
 * (`forTypeIds`) with its KPI, Deliverable, Objective or Structural key. Choose these rows as the
 * default (or select their viewName); registration alone does not create or migrate stored rows.
 * The legacy mixed dashboard views are removed; stored rows must use entity-specific keys.
 * Registry key strings remain stable when component folders/names change, to preserve saved rows.
 */
export const DashboardCardViewKeys = {
  KpiSmall: 'benzaiten.kpi.card.small',
  DeliverableSmall: 'benzaiten.deliverable.card.small',
  ObjectiveSmall: 'benzaiten.objective.card.small',
  StructuralSmall: 'benzaiten.structural.card.small',
  KpiMedium: 'benzaiten.kpi.card.medium',
  DeliverableMedium: 'benzaiten.deliverable.card.medium',
  ObjectiveMedium: 'benzaiten.objective.card.medium',
  StructuralMedium: 'benzaiten.structural.card.medium',
  KpiLarge: 'benzaiten.kpi.card.large',
  DeliverableLarge: 'benzaiten.deliverable.card.large',
  ObjectiveLarge: 'benzaiten.objective.card.large',
  StructuralLarge: 'benzaiten.structural.card.large',
  KpiFull: 'benzaiten.kpi.card.full',
  DeliverableFull: 'benzaiten.deliverable.card.full',
  ObjectiveFull: 'benzaiten.objective.card.full',
  StructuralFull: 'benzaiten.structural.card.full',
} as const;

/**
 * Benzaiten's own entity-view registry (Req 5, D64): app-specific views, keyed
 * `benzaiten.<name>.<kind>.<size>`. For typed Small/Medium/Large cards, configure a Card row at
 * the matching size per entity type (`forTypeIds`) with its KPI, Deliverable, Objective or
 * Structural key. Choose these rows as the default (or select their viewName); registration alone
 * does not create or migrate stored rows. The legacy mixed dashboard views are removed; stored
 * rows must use entity-specific keys. Registry key strings remain stable when component
 * folders/names change, to preserve saved rows. Registered via `provideEntityViewRegistry` in
 * app.config.ts alongside the framework's own `appFrameworkRegistry`.
 */
export const benzaitenEntityViewRegistry: IEntityViewComponent[] = [
  {
    key: 'benzaiten.detail.detail.overlay',
    displayName: 'Benzaiten Detail (Overlay)',
    kinds: [ViewKind.Detail],
    sizes: [DetailSize.Overlay],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../Basic entites/detail/overlay/benzaiten-detail-overlay/benzaiten-detail-overlay.component').then((m) => m.BenzaitenDetailOverlayComponent),
  },
  {
    key: 'benzaiten.deliverable.detail.overlay',
    displayName: 'Deliverable Detail (Overlay)',
    kinds: [ViewKind.Detail],
    sizes: [DetailSize.Overlay],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../deliverable/detail/overlay/deliverable-detail-overlay/deliverable-detail-overlay.component').then((m) => m.DeliverableDetailOverlayComponent),
  },
  {
    key: 'benzaiten.kpi.detail.overlay',
    displayName: 'KPI Detail (Overlay)',
    kinds: [ViewKind.Detail],
    sizes: [DetailSize.Overlay],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../kpi/detail/overlay/kpi-detail-overlay/kpi-detail-overlay.component').then((m) => m.KpiDetailOverlayComponent),
  },
  {
    key: 'benzaiten.measure.detail.overlay',
    displayName: 'Measure Detail (Overlay)',
    kinds: [ViewKind.Detail],
    sizes: [DetailSize.Overlay],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../measure/detail/overlay/measure-detail-overlay/measure-detail-overlay.component').then((m) => m.MeasureDetailOverlayComponent),
  },
  {
    key: 'benzaiten.card.card.medium',
    displayName: 'Benzaiten Card (Medium)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Medium],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../Basic entites/card/medium/benzaiten-card-medium.component').then((m) => m.BenzaitenCardMediumComponent),
  },
  {
    key: 'benzaiten.kpi.card.small',
    displayName: 'KPI Card (Small)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Small],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../kpi/card/small/kpi-card-small.component').then(m => m.KpiCardSmallComponent),
  },
  {
    key: 'benzaiten.deliverable.card.small',
    displayName: 'Deliverable Card (Small)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Small],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../deliverable/card/small/deliverable-card-small.component').then(m => m.DeliverableCardSmallComponent),
  },
  {
    key: 'benzaiten.objective.card.small',
    displayName: 'Objective Card (Small)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Small],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../objective/card/small/objective-card-small.component').then(m => m.ObjectiveCardSmallComponent),
  },
  {
    key: 'benzaiten.structural.card.small',
    displayName: 'Structural Card (Small)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Small],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../structural/card/small/structural-card-small.component').then(m => m.StructuralCardSmallComponent),
  },
  {
    key: 'benzaiten.kpi.card.medium',
    displayName: 'KPI Card (Medium)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Medium],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../kpi/card/medium/kpi-card-medium.component').then(m => m.KpiCardMediumComponent),
  },
  {
    key: 'benzaiten.deliverable.card.medium',
    displayName: 'Deliverable Card (Medium)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Medium],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../deliverable/card/medium/deliverable-card-medium.component').then(m => m.DeliverableCardMediumComponent),
  },
  {
    key: 'benzaiten.objective.card.medium',
    displayName: 'Objective Card (Medium)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Medium],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../objective/card/medium/objective-card-medium.component').then(m => m.ObjectiveCardMediumComponent),
  },
  {
    key: 'benzaiten.structural.card.medium',
    displayName: 'Structural Card (Medium)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Medium],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../structural/card/medium/structural-card-medium.component').then(m => m.StructuralCardMediumComponent),
  },
  {
    key: 'benzaiten.kpi.card.large',
    displayName: 'KPI Card (Large)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Large],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../kpi/card/large/kpi-card-large.component').then(m => m.KpiCardLargeComponent),
  },
  {
    key: 'benzaiten.deliverable.card.large',
    displayName: 'Deliverable Card (Large)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Large],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../deliverable/card/large/deliverable-card-large.component').then(m => m.DeliverableCardLargeComponent),
  },
  {
    key: 'benzaiten.objective.card.large',
    displayName: 'Objective Card (Large)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Large],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../objective/card/large/objective-card-large.component').then(m => m.ObjectiveCardLargeComponent),
  },
  {
    key: 'benzaiten.structural.card.large',
    displayName: 'Structural Card (Large)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Large],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../structural/card/large/structural-card-large.component').then(m => m.StructuralCardLargeComponent),
  },
  {
    key: 'benzaiten.kpi.card.full',
    displayName: 'KPI Card (Full)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Full],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../kpi/card/large/kpi-card-large.component').then(m => m.KpiCardLargeComponent),
  },
  {
    key: 'benzaiten.deliverable.card.full',
    displayName: 'Deliverable Card (Full)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Full],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../deliverable/card/large/deliverable-card-large.component').then(m => m.DeliverableCardLargeComponent),
  },
  {
    key: 'benzaiten.objective.card.full',
    displayName: 'Objective Card (Full)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Full],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../objective/card/large/objective-card-large.component').then(m => m.ObjectiveCardLargeComponent),
  },
  {
    key: 'benzaiten.structural.card.full',
    displayName: 'Structural Card (Full)',
    kinds: [ViewKind.Card],
    sizes: [CardSize.Full],
    locationType: EntityViewComponentLocationType.InApplication,
    location: environment.appId,
    loader: () => import('../structural/card/large/structural-card-large.component').then(m => m.StructuralCardLargeComponent),
  },
];
